import { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, AlertOctagon, Activity, Search, Unlock } from "lucide-react";
import { toast } from "sonner";

const SEV = {
  info:     { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Activity },
  warn:     { color: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertOctagon },
  critical: { color: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertOctagon },
};

const EVENT_LABEL = {
  login_success: "Login",
  login_failure: "Failed login",
  login_blocked_locked: "Blocked (locked)",
  login_blocked_deactivated: "Blocked (deactivated)",
  "2fa_failure": "2FA code wrong",
  "2fa_enabled": "2FA enabled",
  "2fa_disabled": "2FA disabled",
  password_change_tenant: "Tenant password changed",
  password_change_employee: "Employee password changed",
  account_unlocked_by_admin: "Account unlocked",
};

export default function SecurityLogPage() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ severity: "all", q: "" });
  const [unlockEmail, setUnlockEmail] = useState("");

  const params = useMemo(() => {
    const p = { limit: 500 };
    if (filters.severity !== "all") p.severity = filters.severity;
    if (filters.q.trim()) p.q = filters.q.trim();
    return p;
  }, [filters]);

  const load = async () => {
    const { data } = await api.get("/security/log", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [params]); // eslint-disable-line

  const unlock = async () => {
    if (!unlockEmail) { toast.error("Enter an email"); return; }
    try {
      await api.post("/security/unlock", { email: unlockEmail });
      toast.success(`Unlocked ${unlockEmail}`);
      setUnlockEmail(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const counts = items.reduce((a, e) => { a[e.severity] = (a[e.severity] || 0) + 1; return a; }, {});

  return (
    <AppLayout title="Security Log" subtitle="Every login, lockout & sensitive admin action">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {["info", "warn", "critical"].map(s => (
          <Card key={s} className="rounded-none p-5 border border-bone">
            <div className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${SEV[s].color.split(" ")[1]}`}>{s}</div>
            <div className="font-display text-3xl tracking-tight">{counts[s] || 0}</div>
          </Card>
        ))}
        <Card className="rounded-none p-5 border border-gold/40 bg-gold/5">
          <div className="micro-label text-gold mb-2">Force Unlock</div>
          <div className="flex gap-2">
            <Input data-testid="unlock-email" placeholder="email@…" value={unlockEmail} onChange={(e) => setUnlockEmail(e.target.value)} />
            <Button data-testid="unlock-button" onClick={unlock} variant="outline" className="rounded-none border-ink hover:bg-ink hover:text-ivory gap-1.5"><Unlock className="h-3.5 w-3.5" /> Unlock</Button>
          </div>
        </Card>
      </div>

      <Card className="rounded-none p-4 mb-5 border border-bone">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Severity</Label>
            <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
              <SelectTrigger data-testid="sec-filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warn">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="sec-filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="email / username / ip / event" className="pl-8" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="rounded-none border border-bone overflow-hidden">
        <table className="w-full text-sm" data-testid="security-log-table">
          <thead className="bg-ivoryDark text-ink">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">When</th>
              <th className="text-left px-4 py-3 font-semibold">Event</th>
              <th className="text-left px-4 py-3 font-semibold">Severity</th>
              <th className="text-left px-4 py-3 font-semibold">Who</th>
              <th className="text-left px-4 py-3 font-semibold">IP</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-slate-400 py-10 italic">No events match these filters</td></tr>
            ) : items.map((e, i) => {
              const meta = SEV[e.severity] || SEV.info;
              const Icon = meta.icon;
              return (
                <tr key={i} className="border-t border-bone hover:bg-ivory">
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono-num whitespace-nowrap">{(e.ts || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-3 font-medium">{EVENT_LABEL[e.event] || e.event}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 border font-semibold ${meta.color}`}>
                      <Icon className="h-3 w-3" /> {e.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {e.actor_username && <div className="text-slate-700 font-medium">@{e.actor_username}</div>}
                    <div className="text-slate-500">{e.actor_email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono-num">{e.ip || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </AppLayout>
  );
}
